import { Form } from '@components/ui/form';
import { useInternalScheduleForm } from '@forms/schedules/internal';
import { RelatedScheduleFormProps } from '../types';
import { useEffect, useState } from 'react';
import { RemoveScheduleDialog } from '../../../remove';
import { useRemoveInternalSchedule } from '@services/schedules/internal/remove';
import { useRescheduler } from '@contexts/rescheduler';
import { OverlapScheduleDialog } from '@components/schedules/dialogs/internal/action/overlap';
import { useTranslation } from 'react-i18next';
import { useToast } from '@hooks/useToast';
import { Conflict } from '@components/schedules/dialogs/internal/publish/types';
import { formatDate } from 'date-fns';
import { formatTime } from '@utils/i18n';

export const RelatedInternalScheduleForm = ({ children, schedule }: RelatedScheduleFormProps) => {
  const rescheduler = useRescheduler();
  const [overlap, setOverlap] = useState(false);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const { t } = useTranslation('app', { keyPrefix: 'messages.success' });
  const { toast } = useToast();

  const { id, task, startsAt, endsAt, status, foreman, saved } = schedule;
  const taskId = task?.id || '';
  const jobId = task?.job?.id || '';

  const { mutate, isLoading } = useRemoveInternalSchedule({
    id,
    jobId,
    taskId,
    callback: () => {
      rescheduler.appendSavedSchedule(schedule);
      rescheduler.setState((prev) => ({
        ...prev,
        relatedSchedules: prev.relatedSchedules.filter((el) => el.id !== id),
      }));
      toast({
        title: t('schedule.remove'),
      });
    },
  });

  const { form, onSubmit, locked } = useInternalScheduleForm({
    successHandler: (resp) => {
      form.reset();
      toast({
        title: t('schedule.update'),
      });
      if (!resp || !rescheduler) return;
      const { data: updatedSchedule, relatedSchedules: relatedFromResp } = resp;
      if (!updatedSchedule) return;

      const isFirst = !rescheduler.relatedSchedules.length;

      rescheduler.setState((prev) => {
        let nextRelated = prev.relatedSchedules;

        if (isFirst && relatedFromResp?.length) {
          nextRelated = [...prev.relatedSchedules, ...relatedFromResp].map((el) =>
            el.task?.id === updatedSchedule.task?.id ? { ...el, ...updatedSchedule } : el,
          );
        } else if (!isFirst) {
          nextRelated = prev.relatedSchedules.map((el) =>
            el.task?.id === updatedSchedule.task?.id ? { ...el, ...updatedSchedule } : el,
          );
        }

        return {
          ...prev,
          relatedSchedules: nextRelated,
          schedule: isFirst && relatedFromResp?.length ? updatedSchedule : prev.schedule,
          open: (isFirst && Boolean(relatedFromResp?.length)) || prev.open,
        };
      });

      if (isFirst) return;
      rescheduler.appendSavedSchedule(updatedSchedule);
    },
    onOverlap: (conflicts) => {
      setOverlap(true);
      setConflicts(conflicts);
    },
    schedule,
  });

  const { setValue } = form;

  useEffect(() => {
    setValue('startsAt', startsAt);
    setValue('endsAt', endsAt);
    setValue('foremanId', foreman?.id || '');
    setValue('confirmed', status === 'approved');
  }, [status, foreman?.id, startsAt, endsAt, saved, setValue]);

  return (
    <>
      <Form {...form}>
        <form
          className="flex flex-col gap-4"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(onSubmit)}>
          {children}
        </form>
        <OverlapScheduleDialog
          open={overlap}
          setOpen={setOverlap}
          onCancel={() => setValue('forceOverlapping', false)}
          onConfirm={() => {
            setValue('forceOverlapping', true);
            void form.handleSubmit(onSubmit)();
          }}
          disabled={locked}
          conflicts={conflicts || []}
          date={formatDate(new Date(startsAt || ''), 'MMM do')}
          name={task?.name}
          summaryDetail={`${[
            formatTime(new Date(startsAt || '')),
            formatTime(new Date(endsAt || '')),
          ].join(' - ')}, ${formatDate(new Date(startsAt || ''), 'MMM do, yyyy')}`}
        />
      </Form>
      <RemoveScheduleDialog
        open={rescheduler.remove === id}
        setOpen={(v) => rescheduler.setState((prev) => ({ ...prev, remove: v ? id : '' }))}
        onConfirm={mutate}
        disabled={isLoading}
      />
    </>
  );
};
